/**
 * Cross-endpoint primitives: the uniform error body, id/subject path params, the shared
 * filter/range query params (ARCHITECTURE §5.2) and the API-wide limits.
 */
import { z } from 'zod';
import { CONTENT_TYPES, LENGTH_UNITS, METRIC_KEYS, POST_SUBTYPE_PATTERN, POST_TYPES, SUBJECT_TYPES, } from '../enums.js';
/* ------------------------------------------------------------------ errors */
/** Uniform error codes carried by `{error:{code,message,details?}}` (§5, §11). */
export const API_ERROR_CODES = [
    'unauthorized',
    'invalid_credentials',
    'rate_limited',
    'validation_error',
    'not_found',
    'conflict',
    'payload_too_large',
    'media_unavailable',
    /** Notion refused or never answered (#47, SPEC § API errors); `details: {kind, retryable}`. */
    'notion_error',
    /** The Telegram Bot API refused or never answered (#53/#55); `details: {retryable}`. */
    'telegram_error',
    /**
     * The AI backend refused or never answered a generation (#83-#85, SPEC `Comment Generation`);
     * 502, message already secret-redacted. `invalid_request` maps to `validation_error` instead.
     */
    'ai_error',
    'internal_error',
];
/* ------------------------------------------------------------------ limits */
/** Default range when `from`/`to` are absent: last 30 days ending now (§5.2). */
export const DEFAULT_RANGE_DAYS = 30;
/** `limit` ceilings per §5.1. */
export const MAX_RUNS_LIMIT = 100;
export const MAX_POSTS_LIMIT = 200;
export const MAX_FEED_LIMIT = 30;
/** Export row cap (§5.1 #29, ASSUMPTION A-9). */
export const EXPORT_ROW_CAP = 50_000;
/** `ui_state` value ceiling, enforced in the API (§4, §5.1 #32). */
export const UI_STATE_MAX_BYTES = 16 * 1024;
/** Server-side truncation length of `PostRowDTO.previewText` (§5.3). */
export const POST_PREVIEW_CHARS = 200;
/**
 * Visible line-break marker inside `PostRowDTO.previewText` (§5.3): the preview collapses runs of
 * spaces/tabs but keeps paragraph structure by spelling every newline as this string. Decorative —
 * consumers that want the flat text back (the CSV export) call `stripPreviewLineBreaks`.
 */
export const POST_PREVIEW_LINE_BREAK = ' ↵ ';
/**
 * A run of markers plus its padding. Adjacent markers share the space between them (a blank line
 * reads `a ↵ ↵ b`, not `a ↵  ↵ b`) and truncation can cut one loose (`a ↵…`), so the padding is
 * matched, never assumed: `replaceAll(POST_PREVIEW_LINE_BREAK, ' ')` leaves both cases behind.
 */
const PREVIEW_LINE_BREAK_RUN = new RegExp(`(?: *${POST_PREVIEW_LINE_BREAK.trim()})+ *`, 'g');
/** Inverse of the marker: the flat one-line preview, with no decoration left in it. */
export function stripPreviewLineBreaks(preview) {
    return preview.replace(PREVIEW_LINE_BREAK_RUN, ' ').replace(/ +…$/, '…').trim();
}
/** Top-posts section size (§8.7). */
export const TOP_POSTS_LIMIT = 10;
/* -------------------------------------------------------------- deep links */
/**
 * The canonical in-app address of one post (§12.1): the post's **own author's** view screen with
 * `?post=`, so a post opened from a group view still links to a place that can render it.
 *
 * It lives in the contract package because three surfaces mint it — the Notion page property, the
 * Telegram View button and the SPA's "Copy link" — and a second spelling would send users to a
 * different address for the same post.
 */
export function postDeepLinkPath(profileId, postId) {
    return `/view/profile/${String(profileId)}?post=${String(postId)}`;
}
/** `postDeepLinkPath` against an absolute origin; a trailing slash on `baseUrl` is dropped. */
export function postDeepLink(baseUrl, profileId, postId) {
    return `${baseUrl.replace(/\/+$/, '')}${postDeepLinkPath(profileId, postId)}`;
}
/**
 * The in-app address of one **caught** post (SPEC `New Post Watching`): the Posts-to-Comment feed
 * with the shared post popup open over it, rather than the author's view screen.
 *
 * It sits beside `postDeepLinkPath` for the same reason that one is here — two surfaces mint it,
 * the Telegram "Open in App" button and the SPA's `new_post` card, and a second spelling would send
 * the two of them to different places for the same detection.
 */
export function watchDeepLinkPath(postId) {
    return `/watch?post=${String(postId)}`;
}
/* ------------------------------------------------------------------ params */
export const IdSchema = z.coerce.number().int().positive();
export const IdParamsSchema = z.object({ id: IdSchema });
/** `:t`/`:id` of the view endpoints (§5.1 #23-#27, #29). */
export const SubjectParamsSchema = z.object({
    t: z.enum(SUBJECT_TYPES),
    id: IdSchema,
});
const csvSplit = (raw) => raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
/** `?flag=1|0|true|false` — query strings carry no booleans. */
export const BooleanParamSchema = z
    .enum(['0', '1', 'true', 'false'])
    .transform((v) => v === '1' || v === 'true');
export const PostTypesParamSchema = z
    .string()
    .transform(csvSplit)
    .pipe(z.array(z.enum(POST_TYPES)).min(1));
export const ContentTypesParamSchema = z
    .string()
    .transform(csvSplit)
    .pipe(z.array(z.enum(CONTENT_TYPES)).min(1));
/**
 * `subtypes=` is validated by shape, not against a closed list: subtypes are an open annotation
 * (§7.4), so a filter for one the build does not know yet must be a legal request that matches
 * nothing rather than a 400.
 */
export const SubtypesParamSchema = z
    .string()
    .transform(csvSplit)
    .pipe(z.array(z.string().regex(POST_SUBTYPE_PATTERN, 'expected a subtype slug')).min(1));
export const IdListParamSchema = z
    .string()
    .transform(csvSplit)
    .pipe(z.array(z.string().regex(/^[1-9][0-9]*$/, 'expected a positive integer id')).min(1))
    .transform((ids) => ids.map((id) => Number(id)));
/* ------------------------------------------------------------ performance */
/**
 * What a performance condition compares against: the author's own baseline (`multiplier`, "≥ 2×")
 * or the plain metric count (`raw`, "≥ 10 comments"). Wire spelling is `x` / `n` — a CSV token is
 * `metric:x|n:min`, and the short form keeps a URL with eight conditions readable.
 */
export const PERF_KINDS = ['multiplier', 'raw'];
/** Conditions per request — a sidebar cap, and the bound on the joins the predicate emits. */
export const MAX_PERF_CONDITIONS = 8;
/** Built from `METRIC_KEYS` so a new metric cannot be filterable everywhere but here. */
const PERF_TOKEN_PATTERN = new RegExp(`^(?:${METRIC_KEYS.join('|')}):(?:x|n):\\d+(?:\\.\\d+)?$`);
function parsePerfToken(token) {
    const [metric, kind, min] = token.split(':');
    return {
        metric: metric,
        kind: kind === 'x' ? 'multiplier' : 'raw',
        min: Number(min),
    };
}
/** The inverse, so the client never spells the wire form itself. */
export function perfConditionToken(condition) {
    return `${condition.metric}:${condition.kind === 'multiplier' ? 'x' : 'n'}:${String(condition.min)}`;
}
/** `perf=reactions:x:2,comments:n:10` — CSV of `metric:kind:min` tokens, AND-combined (§5.2). */
export const PerfParamSchema = z
    .string()
    .transform(csvSplit)
    .pipe(z
    .array(z.string().regex(PERF_TOKEN_PATTERN, 'expected metric:x|n:min'))
    .min(1)
    .max(MAX_PERF_CONDITIONS))
    .transform((tokens) => tokens.map(parsePerfToken));
/**
 * Shared filter/range params for Metrics, Posts and Feed (§5.2).
 *
 * `countedPostTypes` is deliberately absent: it is a setting, never a request param — display
 * filters never move baselines.
 */
export const ViewFilterParamsSchema = z.object({
    from: z.coerce.number().int().optional(),
    to: z.coerce.number().int().optional(),
    postTypes: PostTypesParamSchema.optional(),
    contentTypes: ContentTypesParamSchema.optional(),
    /** Open subtype annotation (§7.4); an unknown value simply matches nothing. */
    subtypes: SubtypesParamSchema.optional(),
    edited: BooleanParamSchema.optional(),
    hasLink: BooleanParamSchema.optional(),
    /** Group views only; ignored for profile views. */
    authorIds: IdListParamSchema.optional(),
    /** Performance conditions, AND-combined (§5.2). */
    perf: PerfParamSchema.optional(),
    /** @deprecated superseded by `perf`; still accepted, folded in as one multiplier condition. */
    perfMetric: z.enum(METRIC_KEYS).optional(),
    /** @deprecated see `perfMetric`. */
    perfMin: z.coerce.number().optional(),
    lenUnit: z.enum(LENGTH_UNITS).optional(),
    lenMin: z.coerce.number().int().nonnegative().optional(),
    lenMax: z.coerce.number().int().nonnegative().optional(),
    hasMedia: BooleanParamSchema.optional(),
    /**
     * Saved-posts membership (SPEC `saved-posts`): `1` = only saved posts, `0` = only unsaved ones.
     * A display filter like every other one here — it never moves a baseline.
     */
    saved: BooleanParamSchema.optional(),
    /** FTS over post text; also matches author name in group views. */
    q: z.string().trim().max(200).optional(),
});
/** Range presets offered by the range control (§8.5 bucketing presets). */
export const RANGE_PRESETS = ['30d', '60d', '90d', '6mo', '12mo', 'all', 'custom'];
//# sourceMappingURL=common.js.map